import useRouter from "common/hooks/use-router";
import useItem from "common/hooks/useItem";
import useList from "common/hooks/useList";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import Header from "common/components/Header/Header";
import FilteredList from "common/components/Table/FilteredList";
import useAuth from "common/hooks/useAuth";
import AlertsContext from "common/providers/alerts";
import usePark from "common/hooks/usePark";
import ToggleSwitch from "common/components/ToggleSwitch/ToggleSwitch";
import { TrainingExercise } from "types/TrainingExercise";
import { FiBox } from "react-icons/fi";
import SpecySelect from "components/select/SpecySelect/SpecySelect";
import TrainingExerciseItem from "./TrainingExerciseItem";

function TrainingExercises() {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { setAlert } = useContext(AlertsContext);
  const { park } = usePark();

  const { user: currentUser } = useAuth();

  const cantManageTrainingExercises =
    (currentUser?.isAdmin ||
      currentUser?.isSuperadmin ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        currentUser?.administrationPermissions?.["training-grid"]?.create)) ??
    false;

  const {
    fetchItems,
    items,
    isFetching,
    filters,
    setFilter,
    pageCount,
    setPageIndex,
    pageIndex,
    removeListItem,
    itemsCount,
    itemsPerPage,
    error,
  } = useList<TrainingExercise>("training-exercises", {
    defaultFilters: park?.id
      ? {
          park: park.id,
        }
      : {}, // TODO ajust
    defaultSort: "name:ASC",
  });

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]);

  const { removeItem } = useItem<TrainingExercise>("training-exercises", "");

  const handleDelete = useCallback(
    async (id: any) => {
      try {
        removeListItem(id);
        await removeItem(id);
        fetchItems();
      } catch (e) {
        setAlert(e, "danger");
        return e;
      }
      return true;
    },
    [removeListItem, fetchItems, removeItem, setAlert]
  );

  const handleNavigate = (id: any) => {
    navigate(`/training-exercises/edit/${id}`);
  };

  const columns = useMemo(
    () => [
      {
        Header: t("forms.name"),
        filterName: "name$containsi",
        filterLabel: t("filters.search"),
        columnClasses: "md:flex-[2]",
      },
      {
        Header: t("forms.specy"),
        filterName: "species",
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <SpecySelect isFilter allowReadOnlyOptions {...props} />
        ),
      },
      {
        Header: t("common.isArchived"),
        filterName: "isArchived",
        filterComponent: (props: any) => (
          <div className="pt-2">
            <ToggleSwitch
              rightLabel={t("trainingExercices.isArchived")}
              {...props}
            />
          </div>
        ),
        // headerClasses: "mb-3",
      },
    ],
    [t]
  );

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500">
      <Header
        title={t("titlePages.trainingExercises")}
        subtitle={t("titleLegends.trainingExercises")}
        btnTitle={t("newItem.trainingExercise")}
        btnSubtitle={t("newItem.trainingExerciseLegend")}
        Icon={FiBox}
        addLink="/training-exercises/add"
        addLinkDisabled={!cantManageTrainingExercises}
        isBeforeFilters
      />

      <FilteredList
        data={items ?? []}
        columns={columns} // used as filter items
        isFetching={isFetching}
        pageCount={pageCount}
        filters={filters}
        setFilter={setFilter}
        setPageIndex={setPageIndex}
        pageIndex={pageIndex}
        RenderItem={TrainingExerciseItem}
        onDelete={handleDelete}
        onNavigate={handleNavigate}
        itemsCount={itemsCount}
        itemsPerPage={itemsPerPage}
      />
    </div>
  );
}

export default TrainingExercises;
